import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { api, ensureOk, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { BotonDescarga } from '@/components/BotonDescarga';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga } from '@/components/Estados';

import { CATEGORIAS, MODOS, useExamen, type ExamenDetalle } from './queries';

function Detalle({ e }: { e: ExamenDetalle }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const editable = useCan('preguntas', 'editar');
  const [nombre, setNombre] = useState(e.name);
  const [modo, setModo] = useState(e.mode ?? 'enarm');
  const [ids, setIds] = useState(e.questions.map((q) => q.id).join(', '));
  const [borrar, setBorrar] = useState(false);
  const ruta = { params: { path: { id: String(e.id) } } };
  const ok = (msg: string) => (d: ExamenDetalle) => {
    qc.setQueryData(['examenes', e.id], d);
    void qc.invalidateQueries({ queryKey: ['examenes', 'lista'] });
    toast.success(msg);
  };
  const guardar = useMutation({
    mutationFn: () =>
      unwrap<ExamenDetalle>(
        api.PATCH('/api/v1/admin/exams/{id}', {
          ...ruta,
          body: { name: nombre, mode: modo as 'enarm' | 'entrenamiento' },
        }),
      ),
    onSuccess: ok('Examen guardado.'),
    onError: (err) => toast.error(mensajeDe(err)),
  });
  const guardarPreguntas = useMutation({
    mutationFn: () =>
      unwrap<ExamenDetalle>(
        api.PUT('/api/v1/admin/exams/{id}/questions', {
          ...ruta,
          body: {
            questionIds: ids
              .split(/[\s,]+/)
              .map(Number)
              .filter((n) => n > 0),
          },
        }),
      ),
    onSuccess: ok('Preguntas actualizadas.'),
    onError: (err) => toast.error(mensajeDe(err)),
  });
  const eliminar = useMutation({
    mutationFn: () => ensureOk(api.DELETE('/api/v1/admin/exams/{id}', ruta)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['examenes', 'lista'] });
      toast.success('Examen eliminado.');
      void navigate('/preguntas/examenes', { replace: true });
    },
    onError: (err) => toast.error(mensajeDe(err)),
  });
  return (
    <>
      <Link
        to="/preguntas/examenes"
        className="mb-3 inline-flex items-center gap-1 text-sm font-semibold"
      >
        <ArrowLeft size={16} aria-hidden /> Exámenes
      </Link>
      <Encabezado
        titulo={e.name}
        descripcion={`${e.questionCount} casos · ${CATEGORIAS[e.category ?? ''] ?? e.category ?? ''}`}
        acciones={
          <>
            <BotonDescarga
              etiqueta="PDF"
              nombre={`examen-${e.id}.pdf`}
              pedir={() =>
                api.GET('/api/v1/admin/exams/{id}/pdf', {
                  params: { path: { id: String(e.id) }, query: { answers: 'false' } },
                  parseAs: 'blob',
                })
              }
            />
            <BotonDescarga
              etiqueta="PDF con respuestas"
              nombre={`examen-${e.id}-respuestas.pdf`}
              pedir={() =>
                api.GET('/api/v1/admin/exams/{id}/pdf', {
                  params: { path: { id: String(e.id) }, query: { answers: 'true' } },
                  parseAs: 'blob',
                })
              }
            />
            {editable && (
              <button type="button" className="btn-cs btn-peligro" onClick={() => setBorrar(true)}>
                Eliminar
              </button>
            )}
          </>
        }
      />
      <div className="grid gap-6 xl:grid-cols-[2fr_3fr]">
        <div className="space-y-6">
          <section className="panel-vidrio space-y-4">
            <Campo etiqueta="Nombre">
              <input
                className="input-vidrio"
                disabled={!editable}
                value={nombre}
                onChange={(ev) => setNombre(ev.target.value)}
              />
            </Campo>
            <Campo etiqueta="Modo">
              <select
                className="input-vidrio"
                disabled={!editable}
                value={modo}
                onChange={(ev) => setModo(ev.target.value)}
              >
                {Object.entries(MODOS).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Campo>
            {editable && (
              <button
                type="button"
                className="btn-cs btn-primario"
                disabled={guardar.isPending}
                onClick={() => guardar.mutate()}
              >
                Guardar
              </button>
            )}
          </section>
          <section className="panel-vidrio space-y-2">
            <h2 className="text-lg font-extrabold">Reparto por especialidad</h2>
            <ul className="text-sm">
              {e.bySpecialty.map((b) => (
                <li key={b.name} className="flex justify-between">
                  <span>{b.name}</span>
                  <span className="tabular font-bold">{b.count}</span>
                </li>
              ))}
            </ul>
          </section>
          {editable && (
            <section className="panel-vidrio space-y-3">
              <h2 className="text-lg font-extrabold">Cambiar preguntas</h2>
              <Campo etiqueta="IDs en orden (separados por coma o espacio)">
                <textarea
                  className="input-vidrio min-h-32 font-mono text-xs"
                  value={ids}
                  onChange={(ev) => setIds(ev.target.value)}
                />
              </Campo>
              <button
                type="button"
                className="btn-cs btn-contorno"
                disabled={guardarPreguntas.isPending}
                onClick={() => guardarPreguntas.mutate()}
              >
                Guardar preguntas
              </button>
            </section>
          )}
        </div>
        <section className="panel-vidrio max-h-[70dvh] overflow-y-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Caso</th>
                <th>Especialidad</th>
              </tr>
            </thead>
            <tbody>
              {e.questions.map((q, i) => (
                <tr key={q.id}>
                  <td className="tabular">{i + 1}</td>
                  <td>
                    <Link to={`/preguntas/${q.id}`}>
                      #{q.id} · {q.prompt.slice(0, 100)}
                    </Link>
                  </td>
                  <td>{q.specialtyName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
      <ConfirmarAccion
        abierto={borrar}
        onCerrar={() => setBorrar(false)}
        titulo="Eliminar examen"
        descripcion="Se quita de la biblioteca y de las licencias. No se puede si ya tiene intentos de alumnos."
        textoBoton="Eliminar"
        peligro
        ocupado={eliminar.isPending}
        onConfirmar={() => eliminar.mutate()}
      />
    </>
  );
}

export function ExamenPage() {
  const { id } = useParams();
  const q = useExamen(Number(id));
  return (
    <PermissionGate seccion="preguntas">
      {q.isPending ? (
        <Cargando />
      ) : q.isError ? (
        <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />
      ) : (
        <Detalle key={q.data.questionCount + q.data.name} e={q.data} />
      )}
    </PermissionGate>
  );
}
